<template>
  <v-app>
    <AppNavigation v-model:drawer="drawer" />
    
    <v-app-bar color="primary" dark>
      <v-app-bar-nav-icon
        v-if="authStore.isAuthenticated"
        @click="toggleDrawer"
        :aria-label="$t('common.toggleMenu')"
        :title="$t('common.toggleMenu')"
      />
      
      <v-app-bar-title class="text-truncate">{{ authStore.appName }}</v-app-bar-title>
      
      <v-spacer></v-spacer>
      
      <!-- Desktop controls -->
      <div class="d-none d-sm-flex align-center ga-2">
        <v-btn icon @click="toggleTheme" :aria-label="$t('common.toggleTheme')" :title="$t('common.toggleTheme')">
          <v-icon>{{ themeIcon }}</v-icon>
        </v-btn>
        
        <v-select
          v-model="appLocale"
          :items="languageItems"
          item-title="title"
          item-value="value"
          density="compact"
          variant="outlined"
          hide-details
          style="max-width: 140px"
          prepend-inner-icon="mdi-translate"
          :title="$t('common.toggleLanguage')"
        />

        <!-- Auth buttons when not logged in -->
        <template v-if="authStore.authEnabled && !authStore.isAuthenticated && !(isJoinRoute && teamSessionExists)">
          <v-btn to="/login" variant="text" class="text-none">
            <v-icon start>mdi-login</v-icon>{{ $t('auth.login') }}
          </v-btn>
          <v-btn
            v-if="authStore.userRegistrationEnabled"
            to="/register"
            variant="outlined"
            class="text-none"
          >
            <v-icon start>mdi-account-plus</v-icon>{{ $t('register.title') }}
          </v-btn>
        </template>

        <!-- Logout for public join view -->
        <v-btn
          v-if="isJoinRoute && teamSessionExists"
          icon
          @click="triggerJoinLogout"
          :aria-label="$t('auth.logout')"
          :title="$t('auth.logout')"
        >
          <v-icon>mdi-logout</v-icon>
        </v-btn>
      </div>

      <!-- Mobile controls menu -->
      <v-menu class="d-sm-none" :close-on-content-click="false">
        <template #activator="{ props }">
          <v-btn icon v-bind="props" :aria-label="$t('common.moreOptions')" :title="$t('common.moreOptions')">
            <v-icon>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item @click="toggleTheme" prepend-icon="mdi-brightness-6">
            <v-list-item-title>{{ $t('common.toggleTheme') }}</v-list-item-title>
          </v-list-item>
          <v-list-item @click.stop>
            <v-select
              v-model="appLocale"
              :items="languageItems"
              item-title="title"
              item-value="value"
              density="compact"
              variant="outlined"
              hide-details
              prepend-inner-icon="mdi-translate"
              :label="$t('common.toggleLanguage')"
            />
          </v-list-item>
          <template v-if="authStore.authEnabled && !authStore.isAuthenticated && !(isJoinRoute && teamSessionExists)">
            <v-list-item :to="{ name: 'login' }" prepend-icon="mdi-login">
              <v-list-item-title>{{ $t('auth.login') }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="authStore.userRegistrationEnabled" :to="{ name: 'register' }" prepend-icon="mdi-account-plus">
              <v-list-item-title>{{ $t('register.title') }}</v-list-item-title>
            </v-list-item>
          </template>
          <v-list-item
            v-if="isJoinRoute && teamSessionExists"
            @click="triggerJoinLogout"
            prepend-icon="mdi-logout"
          >
            <v-list-item-title>{{ $t('auth.logout') }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <v-main>
      <v-container class="py-0">
        <AppBreadcrumb />
      </v-container>
      <router-view />
    </v-main>

    <AppSnackbar />
    <!-- Global Debug Widget (only when enabled and not on team join routes) -->
    <DebugWidget v-if="authStore.debugWidgetEnabled && !isJoinRoute" />
  </v-app>
</template>

<script setup lang="ts">
import { computed, watch, onMounted, ref } from 'vue'
import { useTheme } from 'vuetify'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import AppSnackbar from '@/components/app/AppSnackbar.vue'
import AppBreadcrumb from '@/components/app/AppBreadcrumb.vue'
import AppNavigation from '@/components/app/AppNavigation.vue'
import { getPageTitle } from '@/utils/breadcrumb'
import DebugWidget from '@/components/debug/DebugWidget.vue'

const theme = useTheme()
const { locale, t } = useI18n()
const route = useRoute()
// const router = useRouter() // Unused
const authStore = useAuthStore()
// Note: console capture now starts from DebugWidget when enabled.

// Navigation drawer state
const drawer = ref(false)

// Update document title to include the current page title
const updateDocumentTitle = () => {
  const pageTitle = getPageTitle(route, t)
  if ((route.name as string) === 'home') {
    document.title = authStore.appName
  } else {
    document.title = `${pageTitle} - ${authStore.appName}`
  }
}

// Watch for relevant changes and update title
watch(() => authStore.appName, updateDocumentTitle, { immediate: true })
watch(() => route.name, updateDocumentTitle)
watch(() => locale.value, updateDocumentTitle)

// Ensure auth config is loaded on mount
onMounted(async () => {
  await authStore.checkAuthConfig()
  updateDocumentTitle()
})

const themeIcon = computed(() => 
  theme.global.name.value === 'dark' ? 'mdi-brightness-7' : 'mdi-brightness-4'
)

const toggleTheme = () => {
  theme.global.name.value = theme.global.name.value === 'dark' ? 'light' : 'dark'
}

const toggleDrawer = () => {
  drawer.value = !drawer.value
}

// Public play route helpers
const isJoinRoute = computed(() => {
  const n = String(route.name || '')
  return n.startsWith('play-') || n === 'play-gameplay'
})
const teamSessionExists = computed(() => {
  if (!isJoinRoute.value) return false
  const publicId = route.params.publicId as string
  if (!publicId) return false
  const sessionKey = `team_session_${publicId}`
  const exists = !!localStorage.getItem(sessionKey)
  return exists
})

function triggerJoinLogout() {
  window.dispatchEvent(new CustomEvent('gameshow-logout'))
}

// Language select items and binding
const languageItems = [
  { title: 'English', value: 'en' },
  { title: 'Deutsch', value: 'de' },
]

const appLocale = computed({
  get: () => locale.value,
  set: (val: string) => { locale.value = val },
})
</script>

<style>
/* Reserve space for the debug panel when open */
main.v-main {
  margin-bottom: var(--debug-panel-height, 0);
}
</style>
